import React, { useState, useEffect } from "react";
import { Menu, X, Instagram, Phone, CalendarSearch } from "lucide-react";
import { NavLink, Link } from "react-router-dom";
import { BRAND } from "../mock/mock";
import { useBooking } from "../context/BookingContext";
import TrackBookingModal from "./TrackBookingModal";

const LINKS = [
  ["Home", "/"],
  ["About", "/about"],
  ["Services", "/services"],
  ["Portfolio", "/portfolio"],
  ["Pricing", "/pricing"],
  ["Reviews", "/reviews"],
  ["Contact", "/contact"],
];

export default function Navbar() {
  const { openBook } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [trackOpen, setTrackOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 ${isActive ? "text-gold" : "text-cream/80 hover:text-gold"}`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-xl border-b hairline py-3" : "py-5"}`}
      style={scrolled ? { background: "rgba(247,240,230,0.85)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex flex-col leading-none">
          <img src="/brand/viimii-word-dark.png" alt="VIIMII STUDIO" className="h-8 md:h-9 w-auto" />
          <span className="section-line text-[10px] text-gold-soft mt-1.5">Nails · Lashes · Hair Spa</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map(([label, to]) => (
            <NavLink key={label} to={to} className={linkCls} end={to === "/"}>
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setTrackOpen(true)}
            data-testid="track-booking-nav-btn"
            className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border hairline text-cream/80 hover:text-gold transition-all duration-300"
          >
            <CalendarSearch size={16} /> Track Booking
          </button>
          <a
            href={BRAND.instagram}
            target="_blank"
            rel="noreferrer"
            className="hidden md:inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-full border hairline text-gold hover:bg-gold hover:text-black transition-all duration-300"
            aria-label="Instagram"
          >
            <Instagram size={16} /> {BRAND.instagramHandle}
          </a>
          <button
            onClick={openBook}
            className="hidden sm:inline-flex items-center gap-2 text-black text-sm font-medium px-5 py-2.5 rounded-full transition-transform hover:scale-105"
            style={{ background: "var(--viimii-gold)" }}
          >
            <Phone size={15} /> Book Now
          </button>
          <button className="lg:hidden text-cream" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden mt-3 mx-4 rounded-2xl bg-viimii-2 border hairline p-5 flex flex-col gap-1">
          {LINKS.map(([label, to]) => (
            <NavLink
              key={label}
              to={to}
              end={to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `py-3 border-b hairline transition-colors ${isActive ? "text-gold" : "text-cream/85 hover:text-gold"}`}
            >
              {label}
            </NavLink>
          ))}
          <a href={BRAND.instagram} target="_blank" rel="noreferrer" className="py-3 border-b hairline text-cream/85 hover:text-gold flex items-center gap-2">
            <Instagram size={16} /> Follow {BRAND.instagramHandle}
          </a>
          <button
            onClick={() => { setOpen(false); setTrackOpen(true); }}
            data-testid="track-booking-nav-btn-mobile"
            className="py-3 border-b hairline text-cream/85 hover:text-gold flex items-center gap-2 text-left"
          >
            <CalendarSearch size={16} /> Track My Booking
          </button>
          <button
            onClick={() => { setOpen(false); openBook(); }}
            className="mt-4 text-black font-medium py-3 rounded-full"
            style={{ background: "var(--viimii-gold)" }}
          >
            Book Your Appointment
          </button>
        </div>
      )}
      <TrackBookingModal open={trackOpen} onClose={() => setTrackOpen(false)} />
    </header>
  );
}
